// src/pages/Giustificativi.jsx
import React, { useEffect, useState } from 'react';
import { authFetch } from '../utils/authFetch';
import { Card } from '../components/ui';

//const API_BASE = 'http://localhost:3001'; // stesso usato in CalendarioAziendale
const API_BASE = '/api'; // stesso usato in CalendarioAziendale

const TIPI = [
  { value: 'ferie', label: 'Ferie' },
  { value: 'rol', label: 'ROL' },
  { value: 'permesso', label: 'Permesso' },
];

export default function Giustificativi() {
  const [form, setForm] = useState({
    type: 'ferie',
    from: '',
    to: '',
    note: '',
  });

  const [justifications, setJustifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | pending | approved | rejected

  const role = localStorage.getItem('role') || 'tecnico';
  const isManager =
    role === 'admin' || role === 'segreteria' || role === 'gestione';

  // ---- Helpers ----
  const formatDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString('it-IT');
  };

  // ---- Caricamento iniziale / filtraggio per stato ----
  const loadJustifications = async () => {
    setLoading(true);
    setError('');
    setInfo('');

    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const url =
        params.toString().length > 0
          ? `${API_BASE}/justificativi?${params.toString()}`
          : `${API_BASE}/justificativi`;

      const res = await authFetch(url);
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error || 'Errore nel recupero dei giustificativi.'
        );
      }

      const data = await res.json();
      setJustifications(data || []);
    } catch (err) {
      console.error('Errore caricamento giustificativi:', err);
      setError(err.message || 'Errore nel recupero dei giustificativi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJustifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // ---- Gestione form nuovo giustificativo ----
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (!form.type || !form.from || !form.to) {
      setError('Compila almeno il tipo e le date Dal / Al.');
      return;
    }

    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/justificativi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: form.type,
          from: form.from,
          to: form.to,
          note: form.note,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error || 'Errore nel salvataggio del giustificativo.'
        );
      }

      const created = await res.json();
      setJustifications((prev) => [...prev, created]);
      setForm({
        type: 'ferie',
        from: '',
        to: '',
        note: '',
      });
      setInfo('Giustificativo registrato correttamente (in attesa di approvazione).');
    } catch (err) {
      console.error('Errore salvataggio giustificativo:', err);
      setError(err.message || 'Errore nel salvataggio del giustificativo.');
    } finally {
      setSaving(false);
    }
  };

  // ---- Approva / Rifiuta (solo manager) ----
  const handleStatusAction = async (id, action) => {
    if (!isManager) return;
    setError('');
    setInfo('');
    setActionLoadingId(id);

    try {
      const url = `${API_BASE}/justificativi/${id}/${action}`;
      const res = await authFetch(url, { method: 'PATCH' });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error ||
            `Errore nell'operazione di ${action === 'approve' ? 'approvazione' : 'rifiuto'}.`
        );
      }

      const updated = await res.json();
      setJustifications((prev) =>
        prev.map((j) => (j._id === updated._id ? updated : j))
      );
      setInfo(
        `Giustificativo ${
          action === 'approve' ? 'approvato' : 'rifiutato'
        } correttamente.`
      );
    } catch (err) {
      console.error('Errore azione giustificativo:', err);
      setError(
        err.message ||
          `Errore durante ${
            action === 'approve' ? "l'approvazione" : 'il rifiuto'
          } del giustificativo.`
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="max-w-6xl mx-auto px-4 pt-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
          Giustificativi Ferie / ROL
        </h1>
        <p className="text-sm text-gray-600 mb-6">
          Registra richieste di ferie, permessi e ROL. Gli utenti con ruolo{' '}
          <span className="font-semibold">Admin / Segreteria / Gestione</span>{' '}
          possono approvare o rifiutare le richieste, che verranno poi visualizzate
          nel calendario aziendale.
        </p>

        <div className="grid lg:grid-cols-[1.2fr,1.8fr] gap-6">
          {/* FORM NUOVO GIUSTIFICATIVO */}
          <Card className="shadow-sm p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              Nuovo giustificativo
            </h2>
            <p className="text-xs text-gray-500 mb-3">
              Il giustificativo verrà associato automaticamente al tuo account
              utente. Compila il tipo, le date e, se vuoi, una nota.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3 text-sm">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Tipo giustificativo
                </label>
                <select
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {TIPI.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Dal
                  </label>
                  <input
                    type="date"
                    name="from"
                    value={form.from}
                    onChange={handleChange}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Al
                  </label>
                  <input
                    type="date"
                    name="to"
                    value={form.to}
                    onChange={handleChange}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Note
                </label>
                <textarea
                  name="note"
                  value={form.note}
                  onChange={handleChange}
                  rows={3}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Eventuali dettagli aggiuntivi…"
                />
              </div>

              <button
                type="submit"
                disabled={saving}
                className={`mt-2 px-4 py-2 rounded-lg text-sm font-semibold text-white ${
                  saving
                    ? 'bg-blue-300 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {saving ? 'Salvataggio…' : 'Aggiungi giustificativo'}
              </button>
            </form>
          </Card>

          {/* LISTA GIUSTIFICATIVI */}
          <Card className="shadow-sm p-4 overflow-x-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-800">
                Giustificativi registrati
              </h2>

              {/* Filtro stato (solo manager ha senso filtrare, ma lo lasciamo per tutti) */}
              <div className="flex items-center gap-2 text-xs md:text-sm">
                <span className="text-gray-500">Stato:</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="all">Tutti</option>
                  <option value="pending">In attesa</option>
                  <option value="approved">Approvati</option>
                  <option value="rejected">Rifiutati</option>
                </select>
              </div>
            </div>

            {loading ? (
              <p className="text-sm text-gray-500">Caricamento giustificativi…</p>
            ) : justifications.length === 0 ? (
              <p className="text-sm text-gray-500">
                Non ci sono giustificativi per il filtro selezionato.
              </p>
            ) : (
              <table className="min-w-full text-xs md:text-sm">
                <thead>
                  <tr className="text-left text-gray-600 border-b">
                    <th className="py-2 pr-2">Dipendente</th>
                    <th className="py-2 pr-2">Tipo</th>
                    <th className="py-2 pr-2">Dal</th>
                    <th className="py-2 pr-2">Al</th>
                    <th className="py-2 pr-2">Note</th>
                    <th className="py-2 pr-2">Stato</th>
                    {isManager && <th className="py-2 text-right">Azioni</th>}
                  </tr>
                </thead>
                <tbody>
                  {justifications.map((j) => {
                    const userLabel =
                      j.user?.username ||
                      j.user?.email ||
                      'Dipendente';

                    return (
                      <tr key={j._id} className="border-b last:border-0">
                        <td className="py-1 pr-2">{userLabel}</td>
                        <td className="py-1 pr-2 capitalize">{j.type}</td>
                        <td className="py-1 pr-2">{formatDate(j.from)}</td>
                        <td className="py-1 pr-2">{formatDate(j.to)}</td>
                        <td
                          className="py-1 pr-2 max-w-[200px] truncate"
                          title={j.note}
                        >
                          {j.note}
                        </td>
                        <td className="py-1 pr-2">
                          <span
                            className={
                              'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ' +
                              (j.status === 'approved'
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : j.status === 'rejected'
                                ? 'bg-red-50 text-red-700 border-red-200'
                                : 'bg-amber-50 text-amber-700 border-amber-200')
                            }
                          >
                            {j.status}
                          </span>
                        </td>

                        {isManager && (
                          <td className="py-1 pr-0 text-right">
                            {j.status === 'pending' ? (
                              <div className="inline-flex gap-1">
                                <button
                                  type="button"
                                  disabled={actionLoadingId === j._id}
                                  onClick={() =>
                                    handleStatusAction(j._id, 'approve')
                                  }
                                  className={`px-2 py-1 rounded-lg text-[11px] font-semibold text-white ${
                                    actionLoadingId === j._id
                                      ? 'bg-emerald-300 cursor-not-allowed'
                                      : 'bg-emerald-600 hover:bg-emerald-700'
                                  }`}
                                >
                                  Approva
                                </button>
                                <button
                                  type="button"
                                  disabled={actionLoadingId === j._id}
                                  onClick={() =>
                                    handleStatusAction(j._id, 'reject')
                                  }
                                  className={`px-2 py-1 rounded-lg text-[11px] font-semibold text-white ${
                                    actionLoadingId === j._id
                                      ? 'bg-red-300 cursor-not-allowed'
                                      : 'bg-red-600 hover:bg-red-700'
                                  }`}
                                >
                                  Rifiuta
                                </button>
                              </div>
                            ) : (
                              <span className="text-[11px] text-gray-400">
                                Nessuna azione
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {(error || info) && (
              <div className="mt-3 space-y-2 text-xs">
                {error && (
                  <p className="text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {error}
                  </p>
                )}
                {info && (
                  <p className="text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    {info}
                  </p>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
